import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, X, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * SuggestionBoxButton — floating "Suggest" pill in the bottom-right
 * corner of every page. Click opens a compact form that posts to
 * /api/suggestions/submit. Successful submits trigger:
 *   • a toast thank-you
 *   • a backend admin nudge (Slack/Telegram if wired, otherwise just
 *     logged into db.admin_nudges for the admin bell-icon to pick up)
 *
 * Hidden on `/admin*` and `/editor*` routes — the team doesn't need to
 * suggest things to themselves, and we want the chrome to be calm in
 * the admin tooling.
 */
const CATEGORIES = [
  { v: "feature", l: "New feature" },
  { v: "events", l: "Events / tickets" },
  { v: "gallery", l: "Photos / galleries" },
  { v: "marketplace", l: "Marketplace" },
  { v: "chauffeur", l: "Chauffeur" },
  { v: "general", l: "Something else" },
];

export default function SuggestionBoxButton() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");

  // Hide on admin / editor / studio / driver dashboards — those are
  // back-of-house surfaces where the floating button would be visual
  // noise.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (path.startsWith("/admin") || path.startsWith("/editor") ||
      path.startsWith("/studio") || path.startsWith("/driver/dashboard") ||
      path.startsWith("/partner/dashboard") || path === "/checkout") {
    return null;
  }

  const reset = () => {
    setName(""); setEmail(""); setMessage(""); setCategory("general");
  };

  const submit = async () => {
    if (message.trim().length < 4) {
      toast.error("Tell us a bit more — at least 4 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/suggestions/submit", {
        name: name.trim() || null,
        email: email.trim() || null,
        message: message.trim(),
        category,
        page_url: typeof window !== "undefined" ? window.location.href : null,
      });
      toast.success("Thanks — ARIA will read your note and the team is notified.");
      reset();
      setOpen(false);
    } catch (e) {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <motion.button
        data-testid="suggestion-box-trigger"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-44 right-6 z-[55] flex items-center gap-2 bg-gold text-luxury-black px-4 py-2.5 shadow-lg shadow-gold/30 hover:bg-gold/90 transition-colors font-body text-sm font-medium tracking-wider uppercase"
        aria-label="Submit a suggestion"
        style={{ borderRadius: "999px" }}
      >
        <Lightbulb size={15} />
        <span className="hidden sm:inline text-[11px]">Suggest</span>
      </motion.button>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="suggestion-box-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => !submitting && setOpen(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-luxury-charcoal border border-gold/20 p-6 sm:p-8"
            >
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="text-[10px] font-body uppercase tracking-[0.25em] text-gold mb-1">
                    Suggestion Box
                  </p>
                  <h3 className="text-white font-heading text-xl leading-tight">
                    What would make this better?
                  </h3>
                  <p className="text-white/40 text-xs font-body mt-1.5">
                    ARIA reads every note. The team is paged the moment you hit send.
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="suggestion-box-close"
                  className="text-white/40 hover:text-white p-1"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    data-testid="suggestion-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name (optional)"
                    maxLength={80}
                    className="bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none"
                  />
                  <input
                    data-testid="suggestion-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email (optional)"
                    className="bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none"
                  />
                </div>
                <select
                  data-testid="suggestion-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body focus:border-gold/50 outline-none"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.v} value={c.v}>{c.l}</option>
                  ))}
                </select>
                <textarea
                  data-testid="suggestion-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Your suggestion, idea, or feedback..."
                  rows={5}
                  maxLength={2000}
                  className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2.5 font-body placeholder:text-white/30 focus:border-gold/50 outline-none resize-none"
                />
                <p className="text-white/30 text-[10px] font-body text-right">
                  {message.length}/2000
                </p>
              </div>

              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                  data-testid="suggestion-cancel"
                  className="flex-1 border border-white/10 text-white/60 hover:text-white hover:border-white/20 px-4 py-2.5 font-body text-xs uppercase tracking-wider transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={submitting || message.trim().length < 4}
                  data-testid="suggestion-submit"
                  className="flex-1 bg-gold hover:bg-gold/90 text-luxury-black px-4 py-2.5 font-body text-xs uppercase tracking-wider font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {submitting ? "Sending..." : <>Send <Send size={12} /></>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
